import { Fragment, useRef, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import Button from "../button";
import {
  useGetUserDetailsQuery,
  useAssignProfesionsMutation,
} from "../../store/admin/professionalUserInfo";
import { useGetProfessionsQuery } from "../../store/professions";
import AdminProfessionServices from "./AdminProfessionServices";
import { selectedProfessions } from "../../store/reducers/professionsSlice";

const AdminProfessions = ({
  showModal,
  setShowModal,
  professionalId,
  professionalInfo,
}) => {
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const [inputValue, setInputValue] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [showProfessionServicesModal, setShowProfessionServicesModal] =
    useState(false);
  const [selectedProfession, setSelectedProfession] = useState([]);
  const [filteredProfessions, setFilteredProfessions] = useState([]);
  const [professionFormatData, setProfessionFormatData] = useState();
  const [disabledProfessions, setDisabledProfession] = useState([]);

  const { data: professionsData } = useGetProfessionsQuery();
  const [
    addProfessions,
    { isLoading: addProfessionsLoader, isSuccess: addProfessionsIsSuccess },
  ] = useAssignProfesionsMutation();

  useEffect(() => {
    if (!addProfessionsLoader && addProfessionsIsSuccess) {
      setShowModal(false);
      setShowProfessionServicesModal(true);
    }
  }, [addProfessionsLoader, addProfessionsIsSuccess]);

  useEffect(() => {
    if (professionalInfo?.professions) {
      setDisabledProfession(professionalInfo?.professions);
      setSelectedProfession(professionalInfo?.professions);
    }
  }, [professionalInfo?.professions]);

  useEffect(() => {
    const formattedData = professionsData && formatData(professionsData);
    setFilteredProfessions(formattedData);
    setProfessionFormatData(formattedData);
  }, [professionsData]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    const filtered = professionFormatData
      ?.map((profession) => {
        const filteredChildren = profession.children.filter((child) =>
          child.name.toLowerCase().includes(value.toLowerCase())
        );

        return {
          ...profession,
          children: filteredChildren,
        };
      })
      .filter((profession) => {
        // Include profession if parent matches or if any child matches
        return (
          profession.name.toLowerCase().includes(value.toLowerCase()) ||
          profession.children.length > 0
        );
      });

    setFilteredProfessions(filtered);
  };

  const handleSelectedProfession = (prof) => {
    let updatingProfession = [...selectedProfession];
    let foundItem = selectedProfession.find(
      (profession) => profession.id === prof.id
    );
    if (foundItem) {
      updatingProfession = selectedProfession.filter(
        (profession) => profession.id != prof.id
      );
    } else {
      updatingProfession.push(prof);
    }
    // if (updatingProfession.length < 4) {
    setInputValue("");
    setSelectedProfession(updatingProfession);
    // }
  };

  const formatData = (data) => {
    if (data) {
      const dataMap = {};
      const result = [];

      // Create a map of data objects
      data?.forEach((obj) => {
        dataMap[obj.id] = { ...obj, children: [] };
      });

      data?.forEach((obj) => {
        if (obj.parent_id) {
          // If the object has a parent, add it as a child to the parent
          if (dataMap[obj.parent_id]?.children) {
            dataMap[obj.parent_id].children = [
              ...dataMap[obj.parent_id].children,
              obj,
            ];
            delete dataMap[obj.id];
          }
        }
      });
      Object.keys(dataMap).forEach((key) => {
        const value = dataMap[key];
        result.push(value);
      });
      return result;
    }
  };

  const handleRemoveSelectedProfession = (prof) => {
    setSelectedProfession(
      selectedProfession.filter((profession) => profession.id != prof.id)
    );
  };

 

  const handleNextButtonClick = () => {
    let isNewProfessionsExist = selectedProfession.filter((profession) =>
      disabledProfessions.every(
        (disabledProf) => disabledProf.id != profession.id
      )
    );
    if (isNewProfessionsExist.length > 0) {
      let profession_ids = selectedProfession.map(
        (profession) => profession.id
      );
      const params = {
        professionalId: professionalInfo?.id,
        professions: profession_ids,
      };
      addProfessions(params);
    } else {
      setShowModal(false);
      setShowProfessionServicesModal(true);
    }
  };
  const handleCancelButtonClick = () => {
    setShowModal(false);
  };

  return (
    <>
      <Transition.Root show={showModal} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setShowModal(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-[10px] bg-white p-6 text-left shadow-xl transition-all sm:my-8 w-full max-w-[710px]">
                  <div>
                    <div className="mb-5 flex items-center justify-between gap-5 pb-2">
                      <Dialog.Title
                        as="h3"
                        className="text-lg md:text-[22px] leading-[160%] font-bold text-customGray"
                      >
                        Select a Profession or Specialty to Add:
                      </Dialog.Title>
                      <XMarkIcon
                        className="w-5 sm:w-6 h-5 sm:h-6 cursor-pointer hover:text-red-500"
                        aria-hidden="true"
                        onClick={() => setShowModal(false)}
                      />
                    </div>
                  </div>
                  <div className="flex items-center h-[48px] md:h-[70px] rounded-[10px]">
                    <div className="max-w-722 w-full relative h-full">
                      <div
                        className="rounded-[10px] h-full flex items-center px-3.5 md:px-5 bg-fieldBgColor border border-solid border-strokeColor"
                        ref={inputRef}
                        onClick={() => setShowDropdown(true)}
                      >
                        <div className="border-r border-strokeColor pr-2.5 flex items-center justify-center">
                          <MagnifyingGlassIcon className="w-6 h-6" />
                        </div>
                        <input
                          type="text"
                          placeholder="Search by profession or speciality..."
                          className="md:text-lg text-sm font-medium text-customGray placeholder:text-[#869099] pl-2.5 pr-[25px] bg-transparent"
                          name="profession"
                          value={inputValue}
                          onChange={(e) => {
                            handleInputChange(e);
                          }}
                          autoComplete="off"
                        />
                      </div>
                      {showDropdown &&
                        filteredProfessions?.length > 0 &&
                        inputValue.length > 0 && (
                          <div ref={dropdownRef} className="">
                            <XMarkIcon
                              className="w-5 h-5 text-red-500 absolute z-10 right-2 mt-3 cursor-pointer"
                              onClick={() => setInputValue("")}
                            />
                            <ul className="scroller absolute p-5 top-[50px] md:top-[76px] left-0 right-0 bg-white border border-[#EBEFF3] rounded-lg shadow-lg max-h-[300px] overflow-y-auto">
                              {filteredProfessions
                                ?.filter(
                                  (profession) => profession.parent_id === null
                                )
                                .map((profession) => (
                                  <li
                                    key={profession.id}
                                    className={`${
                                      disabledProfessions.some(
                                        (disabledProf) =>
                                          disabledProf.id === profession.id
                                      )
                                        ? "cursor-default"
                                        : "cursor-pointer"
                                    } profession-list`}
                                  >
                                    <div
                                      className={`form-group relative flex items-center gap-3.5 transition-all duration-300 ease-in ${
                                        disabledProfessions.some(
                                          (disabledProf) =>
                                            disabledProf.id === profession.id
                                        )
                                          ? "cursor-default"
                                          : "cursor-pointer"
                                      } mb-3.5`}
                                    >
                                      <input
                                        id={`parent${profession.id}`}
                                        aria-describedby="comments-description"
                                        name="test"
                                        type="checkbox"
                                        checked={selectedProfession.some(
                                          (prof) => prof.id === profession.id
                                        )}
                                        disabled={disabledProfessions.some(
                                          (disabledProf) =>
                                            disabledProf.id === profession.id
                                        )}
                                        onChange={() =>
                                          handleSelectedProfession(profession)
                                        }
                                        className={`rounded border border-solid border-[#CCD0D4] relative ${
                                          disabledProfessions.some(
                                            (disabledProf) =>
                                              disabledProf.id === profession.id
                                          )
                                            ? "cursor-default"
                                            : "cursor-pointer"
                                        } inline-block w-5 h-5 leading-[0px]`}
                                      />
                                      <label
                                        htmlFor={`parent${profession.id}`}
                                        className={`text-sm font-bold leading-[160%] text-customGray ${
                                          disabledProfessions.some(
                                            (disabledProf) =>
                                              disabledProf.id === profession.id
                                          )
                                            ? "cursor-default"
                                            : "cursor-pointer"
                                        }`}
                                      >
                                        {profession.name}
                                      </label>
                                    </div>
                                    {profession.children?.map((chlid) => (
                                      <ul className="pl-8 mb-4 sub-list">
                                        <li
                                          key={chlid.id}
                                          className="cursor-pointer"
                                        >
                                          <div
                                            className={`form-group relative flex items-center gap-3.5 transition-all duration-300 ease-in cursor-pointer mb-3`}
                                          >
                                            <input
                                              id={`child${chlid.id}`}
                                              aria-describedby="comments-description"
                                              name="test"
                                              type="checkbox"
                                              checked={selectedProfession.some(
                                                (prof) => prof.id === chlid.id
                                              )}
                                              onChange={() =>
                                                handleSelectedProfession(chlid)
                                              }
                                              className="rounded border border-solid border-[#CCD0D4] relative cursor-pointer inline-block w-5 h-5 leading-[0px]"
                                            />
                                            <label
                                              htmlFor={`child${chlid.id}`}
                                              className="text-sm font-normal leading-[160%] text-customGray cursor-pointer"
                                            >
                                              {chlid.name}
                                            </label>
                                          </div>
                                        </li>
                                      </ul>
                                    ))}
                                  </li>
                                ))}
                            </ul>
                          </div>
                        )}
                    </div>
                  </div>
                  <div
                    className={`mt-6 mb-8 flex ${
                      selectedProfession.length > 0 &&
                      selectedProfession.length < 3
                        ? "flex-col sm:flex-row sm:items-center"
                        : "flex-col items-start"
                    } gap-3`}
                  >
                    {selectedProfession.length > 0 && (
                      <span
                        className={`text-base font-medium leading-[160%] text-[#485868] ${
                          selectedProfession.length !== 1 && "w-[156px]"
                        }`}
                      >
                        {selectedProfession.length === 1
                          ? "Your profession is:"
                          : "Your professions are:"}
                      </span>
                    )}
                    <ul
                      className={`flex items-center flex-wrap gap-4 ${
                        selectedProfession.length > 0 &&
                        selectedProfession.length < 3
                          ? "w-full sm:w-[calc(100%-168px)]"
                          : "w-full"
                      }`}
                    >
                      {selectedProfession.length > 0 &&
                        selectedProfession.map((item) => (
                          <li className="h-10 md:h-[54px] rounded-[10px] bg-[#F1F4F6] px-3.5 md:px-5 inline-flex gap-4 items-center justify-center text-sm md:text-base font-medium leading-[160%] text-customGray">
                            {item.name}
                            <XMarkIcon
                              className={`w-5 h-5 ${
                                disabledProfessions.some(
                                  (disabledProf) => disabledProf.id === item.id
                                )
                                  ? "cursor-default pointer-events-none"
                                  : "cursor-pointer hover:text-red-500"
                              }`}
                              onClick={() =>
                                handleRemoveSelectedProfession(item)
                              }
                            />
                          </li>
                        ))}
                    </ul>
                  </div>
                  <div className="flex justify-between items-center flex-col-reverse md:flex-row py-4">
                    <Button
                      type="button"
                      title="cancel"
                      classes="h-[48px] md:h-[58px] flex items-center bg-[#F1F4F6] text-black rounded-[10px] font-black uppercase text-[#485868] tracking-[0.7px] w-full md:w-[160px] justify-center"
                      onClick={handleCancelButtonClick}
                      iconClasses="icon w-6 h-6 -ml-0.5 mr-[10px]"
                    />
                    <Button
                      type="button"
                      title="next"
                      classes={`h-[48px] md:h-[58px] bg-primary text-white rounded-[10px] font-black text-[16px] leading-[160%] uppercase tracking-[0.5px] w-full md:w-[160px] mb-[14px] md:mb-0 duration-500 hover:bg-[#026ed6] flex items-center justify-center max-w-full md:max-w-[143px] md:w-full ${addProfessionsLoader ? 'pointer-events-none' : 'cursor-pointer'}`}
                      onClick={handleNextButtonClick}
                      buttonLoader={addProfessionsLoader}
                    />
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      <AdminProfessionServices
        professionalInfo={professionalInfo}
        selectedProfession={selectedProfession}
        showModal={showProfessionServicesModal}
        setShowModal={setShowProfessionServicesModal}
        previousModal={setShowModal}
      />
    </>
  );
};

export default AdminProfessions;
